import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { erc20Abi, formatUnits, isAddress, parseUnits } from 'viem';
import { useAccount, usePublicClient, useReadContract } from 'wagmi';
import { rewardTokenAddress } from './Home';
import { Web3Button } from './Web3Button';
import { RewardTokenAbi } from './abi/abis';

type Address = `0x${string}`;

export const routerAbi = [
  {
    "type": "function",
    "name": "factory",
    "stateMutability": "nonpayable",
    "inputs": [
    ],
    "outputs": [
      { "type": "address", "name": "" },
    ]
  },
] as const;

export const factoryAbi = [
  {
    "type": "function",
    "name": "getPair",
    "stateMutability": "nonpayable",
    "inputs": [
      { "type": "address", "name": "token0" },
      { "type": "address", "name": "token0" },
    ],
    "outputs": [
      { "type": "address", "name": "" },
    ]
  },
] as const;

export default function RemoveLiquidity() {
  const { address } = useAccount();
  const client = usePublicClient();

  const [dexRouter, setDexRouter] = useState<Address>('0x165C3410fC91EF562C50559f7d2289fEbed552d9'); // PulseX v2 Router
  const [lpTokenAmount, setLpTokenAmount] = useState('0');
  const [lpTokenAddress, setLpTokenAddress] = useState<Address | undefined>();
  const [tokenBAddress, setTokenBAddress] = useState<Address>('0xA1077a294dDE1B09bB078844df40758a5D0f9a27');
  //   const [outputAmountMin, setOutputAmountMin] = useState('0');
  //   const [secondTokenOutputAmountMin, setSecondTokenOutputAmountMin] = useState('0');
  const [recipient, setRecipient] = useState<Address>(address || '0x0000000000000000000000000000000000000000');
  const [deadline, setDeadline] = useState('20'); // 20 minutes from now

  useEffect(() => {
    async function fetchPair() {
      try {
        const factory = await client!.readContract({
          address: dexRouter,
          abi: routerAbi,
          functionName: 'factory',
        });
        const pair = await client!.readContract({
          address: factory,
          abi: factoryAbi,
          functionName: 'getPair',
          args: [rewardTokenAddress, tokenBAddress]
        });
        setLpTokenAddress(pair);
      }
      catch (err) {
        console.error(err);
        setLpTokenAddress(undefined)
      }
    }
    if (client && !!dexRouter && !!tokenBAddress)
      fetchPair();
  }, [client, dexRouter, tokenBAddress]);

  async function setLpAmount(percent: number) {
    try {
      let balance: bigint = 0n;
      if (lpTokenAddress !== undefined) {
        balance = await client!.readContract({
          address: lpTokenAddress,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [address ?? '0x']
        });
        balance = balance * BigInt(percent * 100) / 10000n;
      }
      setLpTokenAmount(formatUnits(balance, 18));
    }
    catch (err) {
      console.error(err);
      setLpTokenAmount('0')
    }
  }

  const { data: tokenAAllowance, refetch: lpTokenAllowanceRefetch } = useReadContract({
    address: lpTokenAddress,
    abi: erc20Abi,
    functionName: "allowance",
    args: [address!, rewardTokenAddress],
    query: {
      enabled: !!address && !!lpTokenAddress,
      refetchInterval: 3000,
    },
  } as const);

  let lpTokenAmountRaw: bigint | undefined;
  try { lpTokenAmountRaw = parseUnits(lpTokenAmount, 18); } catch { }

  const lpTokenApprovalSufficient = tokenAAllowance !== undefined && lpTokenAmountRaw !== undefined && lpTokenAmount !== undefined && tokenAAllowance >= lpTokenAmountRaw;

  const validInput = lpTokenAmountRaw !== undefined && lpTokenAmountRaw > 0n &&
    parseFloat(deadline) > 0 &&
    isAddress(dexRouter) &&
    lpTokenAddress !== undefined && isAddress(lpTokenAddress) &&
    isAddress(tokenBAddress) &&
    isAddress(recipient);

  return (
    <div className="border-2 border-red-900 bg-gradient-to-b from-red-950 p-3 rounded-xl">
      <div className="grid grid-cols-[auto_auto] gap-2 items-center">
        <div className="col-span-2 flex flex-row">
          <div className="label text-bold text-xl grow">Remove V2 Liquidity Without Fees</div>
        </div>

        <div className="label">DEX router address:</div>
        <input className="input w-[25rem]" type="text" value={dexRouter} onChange={(e) => setDexRouter(e.target.value as Address)} />

        {/* <div className="label">LP Token Address:</div> */}
        {/* <input className="input w-[25rem]" type="text" value={lpTokenAddress} onChange={(e) => setLpTokenAddress(e.target.value as Address)} /> */}

        <div className="col-span-2 my-2 border-t border-red-700"></div>

        <div className="label">Secondary token address:</div>
        <input className="input w-[25rem]" type="text" value={tokenBAddress} onChange={(e) => setTokenBAddress(e.target.value as Address)} />

        {/* <div className="label">Min amount of {rewardTokenSymbol} to receive:</div>
        <input className="input w-[25rem]" type="text" value={outputAmountMin} onChange={(e) => setOutputAmountMin(e.target.value)} />

        <div className="label">Min amount of secondary token to receive:</div>
        <input className="input w-[25rem]" type="text" value={secondTokenOutputAmountMin} onChange={(e) => setSecondTokenOutputAmountMin(e.target.value)} /> */}

        <div className="col-span-2 my-2 border-t border-red-700"></div>

        <div className="label">LP token address:</div>
        <input className="input w-[25rem]" type="text" value={lpTokenAddress} disabled={true} />

        <div className="label">Amount of LP tokens to remove:</div>
        <div className="flex flex-row items-center gap-2">
          <input className="input w-[25rem]" type="text" value={lpTokenAmount} onChange={(e) => setLpTokenAmount(e.target.value)} />
          {!(parseFloat(lpTokenAmount) >= 0) && <div className="text-red-700  text-2xl">⚠</div>}
        </div>

        <div className="col-span-2 flex flex-row justify-end gap-2">
          <button className="btn" onClick={() => setLpAmount(25)}>25%</button>
          <button className="btn" onClick={() => setLpAmount(50)}>50%</button>
          <button className="btn" onClick={() => setLpAmount(75)}>75%</button>
          <button className="btn" onClick={() => setLpAmount(100)}>100%</button>
        </div>

        <div className="col-span-2 my-2 border-t border-red-700"></div>

        <div className="label">Recipient address:</div>
        <input className="input w-[25rem]" type="text" value={recipient} onChange={(e) => setRecipient(e.target.value as Address)} />

        <div className="label">Deadline (minutes):</div>
        <input className="input w-24" type="number" value={deadline} onChange={(e) => setDeadline(e.target.value)} />

        <div className="col-span-2 flex flex-row justify-end gap-2">
          {(lpTokenAddress !== undefined && !lpTokenApprovalSufficient) && <Web3Button
            className="w-48"
            contractAddress={lpTokenAddress}
            contractAbi={erc20Abi}
            action={async ({ estimateAndSend }) => {
              const promise = estimateAndSend("approve", [rewardTokenAddress, lpTokenAmountRaw]);
              toast.promise(promise, { pending: 'Approving spending LP tokens...' });
              return await promise;
            }}
            onError={async (err: any) => {
              console.error(err);
              toast.error(`Failed to approve spending LP tokens: ${err.shortMessage ?? err}`);
            }}
            onSuccess={async () => {
              lpTokenAllowanceRefetch();
              toast.success('Spending of LP finished successfully!');
            }}
            disabled={!(lpTokenAmountRaw !== undefined && lpTokenAmountRaw >= 0n)}
          >
            Approve LP token
          </Web3Button>}

          {lpTokenApprovalSufficient && <Web3Button
            className="w-48"
            contractAddress={rewardTokenAddress}
            contractAbi={RewardTokenAbi}
            action={async ({ readContractCustom, estimateAndSend, estimateAndSendCustom }) => {
              const deadlineInSeconds = Math.floor(Date.now() / 1000) + parseInt(deadline) * 60;
              const args = [
                dexRouter,
                lpTokenAmountRaw,
                tokenBAddress,
                0n, // parseUnits(outputAmountMin, 18), // Assuming reward token has 18 decimals
                0n, // parseUnits(secondTokenOutputAmountMin, 18), // Assuming second token has 18 decimals, should fetch dynamically
                recipient,
                BigInt(deadlineInSeconds),
              ];
              console.log("removeLiquidity", args);
              const promise = estimateAndSend("removeLiquidity", args);
              toast.promise(promise, { pending: 'Removing liquidity...' });
              return await promise;
            }}
            onError={async (err: any) => {
              console.error(err);
              toast.error(`Failed to remove liquidity: ${err.shortMessage ?? err}`);
            }}
            onSuccess={async () => {
              toast.success('Liquidity removed successfully!');
              setLpTokenAmount('0');
            }}
            disabled={!validInput}
          >
            Remove Liquidity
          </Web3Button>}
        </div>
      </div>
    </div>
  );
}
